/**
 * NO03 - Fakturakopi Saga
 */

import { put, call } from 'redux-saga/effects';
import fileDownload from 'js-file-download';
import * as actions from '../actions';
import * as actionsNO03 from '../actions/indexNO03';
import * as APICalls from '../../felles/APICalls_NO03';
import NO03 from '../../models/NO03';
import FirmNavnId from '../../models/FirmNavnId';

// The backend's Price is already negative for kreditnotaer (confirmed: the header total, which
// sums the backend's raw Price directly, already comes out negative) — no sign flip needed here,
// it would just double-negate an already-correct negative value back to positive.
const mapInvoiceItem = (item: any): NO03 => {
    const grossPrice = (parseFloat((item.price ?? '0').toString().replace(/\s/g, '').replace(',', '.')) || 0) * 1.25;
    return new NO03(
        item.customerNumber?.toString() ?? '',
        item.customerName ?? '',
        item.invoiceNumber ?? '',
        item.invoiceKID ?? '',
        Array.isArray(item.orderNumbers) ? item.orderNumbers.join(', ') : (item.orderNumbers ?? ''),
        item.invoiceDate ?? '',
        grossPrice
    );
};

/**
 * Search for invoices
 */
export function* searchNO03InvoicesSaga(action: any) {
    try {
        const { payload: searchParams } = action;

        // Add pagination parameters
        const paginatedParams = {
            ...searchParams,
            page: 0,
            pageSize: 100,
        };

        const response = yield call(APICalls.searchInvoices, paginatedParams);

        if (response?.data) {
            const rawInvoices: any[] = response.data.invoices || [];
            const invoices: NO03[] = rawInvoices.map(mapInvoiceItem);
            const hasMore = rawInvoices.length >= 100; // If we got 100 items, there might be more
            const totalAmount = (response.data.totalAmount ?? 0) * 1.25;
            const totalCount = response.data.totalCount ?? invoices.length;

            yield put(actionsNO03.NO03SearchSuccess(invoices, totalAmount, hasMore, totalCount));
        }
    } catch (error: any) {
        yield put(actionsNO03.NO03SearchFailure(error?.message || 'Failed to search invoices'));
        yield put(actions.showError(error?.message || 'Failed to search invoices'));
    }
}

/**
 * Load more invoices (next page)
 */
export function* loadMoreNO03InvoicesSaga(action: any) {
    try {
        const { payload: searchParams } = action;

        const response = yield call(APICalls.searchInvoices, searchParams);

        if (response?.data) {
            const rawInvoices: any[] = response.data.invoices || [];
            const invoices: NO03[] = rawInvoices.map(mapInvoiceItem);
            const hasMore = rawInvoices.length >= 100;
            const totalAmount = (response.data.totalAmount ?? 0) * 1.25;
            const totalCount = response.data.totalCount ?? invoices.length;

            yield put(actionsNO03.NO03LoadMoreSuccess(invoices, hasMore, totalAmount, totalCount));
        }
    } catch (error: any) {
        yield put(actionsNO03.NO03LoadMoreFailure(error?.message || 'Failed to load more invoices'));
    }
}

/**
 * Load firma list for the user
 */
export function* loadNO03FirmaListSaga() {
    try {
        const response = yield call(APICalls.getFirmaList);

        if (response?.data) {
            const firmaList: FirmNavnId[] = response.data.map((item: any) =>
                new FirmNavnId(
                    parseInt(item.firmID, 10) || 0,
                    item.firmName || '',
                    parseInt(item.parentFirmID, 10) || 0,
                    item.firmPrefix || ''
                )
            );
            yield put(actionsNO03.NO03LoadFirmaListSuccess(firmaList));
        }
    } catch (error: any) {
        if (error?.response?.status === 403) {
            yield put(actionsNO03.NO03LoadFirmaListSuccess([]));
            return;
        }
        yield put(actionsNO03.NO03LoadFirmaListFailure(error?.message || 'Failed to load firma list'));
        yield put(actions.showError(error?.message || 'Failed to load firma list'));
    }
}

/**
 * Export invoices to Excel
 */
export function* exportNO03ToExcelSaga(action: any) {
    try {
        yield put(actions.setIsloading(true));
        const { payload: searchParams } = action;

        const response = yield call(APICalls.exportInvoicesToExcel, searchParams);

        if (response?.data) {
            yield fileDownload(response.data, `Fakturakopi_${Date.now()}.xlsx`);
        }
    } catch (error: any) {
        yield put(actions.showError(error?.message || 'Failed to export to Excel'));
    } finally {
        yield put(actions.setIsloading(false));
    }
}
